import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ReactQuill from "react-quill"; // Import React Quill
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import { Helmet } from "react-helmet-async";
import ImageUpload from "./ImageUpload";
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  selectStyles,
  textFieldStyles,
} from "../../../../Styles/Styles";
import AdLimitReachedModal from "./AdLimitReachedModal";
import AdStatusModal from "./AdStatusModal";

const PostAd = () => {
  const [userAdCount, setUserAdCount] = useState(null);
  const [userAdLimit, setUserAdLimit] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]); // State for fetched locations
  const [email, setEmail] = useState("");
  // states for Vehicle Category
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [engineSize, setEngineSize] = useState("");
  const [bodyStyle, setBodyStyle] = useState("");
  const [trimLevel, setTrimLevel] = useState("");
  const [transmission, setTransmission] = useState("");
  const [drivetrain, setDrivetrain] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [mileage, setMileage] = useState("");
  const [mileageUnit, setMileageUnit] = useState("");
  const [condition, setCondition] = useState("");
  const [interiorFeatures, setInteriorFeatures] = useState("");
  // states for Real Estate Category
  const [propertyType, setPropertyType] = useState("");
  const [listingType, setListingType] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [squareFootage, setSquareFootage] = useState("");
  const [lotSize, setLotSize] = useState("");
  const [yearBuilt, setYearBuilt] = useState("");
  const [furnishingStatus, setFurnishingStatus] = useState("");
  const [parking, setParking] = useState("");
  const [floorLevel, setFloorLevel] = useState("");
  const [totalFloors, setTotalFloors] = useState("");
  const [receiveOffers, setReceiveOffers] = useState(false);
  const [minOfferAmount, setMinOfferAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
    adID: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setEmail(userData.email);

      // 🔸 Fetch user ad count
      const fetchUserAdCount = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/user-ads-count`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data = await response.json();
          if (response.ok) {
            setUserAdCount(data.adCount);
            setUserAdLimit(data.adLimit);
          } else {
            console.error("Ad count fetch failed:", data.error);
          }
        } catch (err) {
          console.error("Error fetching ad count:", err);
        }
      };
      fetchUserAdCount();
    }

    // Fetch categories
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const categoriesData = await response.json();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    // Fetch locations
    const fetchLocations = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/locations`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch locations");
        }
        const locationsData = await response.json();
        setLocations(locationsData);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchCategories();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (
      userAdCount !== null &&
      userAdLimit !== null &&
      userAdCount >= userAdLimit
    ) {
      setShowLimitModal(true);
    } else {
      setShowLimitModal(false);
    }
  }, [userAdCount, userAdLimit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    const hasOversizedImage = images.some((img) => img.isOversized);
    if (hasOversizedImage) {
      setLoading(false);
      setError(
        "One or more images exceed the 15MB limit. Please remove them before submitting."
      );
      return;
    }

    try {
      if (!title || !category || !location || !price) {
        throw new Error("Please fill in all required fields.");
      }
      // Create FormData to handle file and other inputs
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("category", category);
      formData.append("location", location); // Append the selected location
      formData.append("email", email);
      // Vehicle Fields
      formData.append("model", model);
      formData.append("year", year);
      formData.append("make", make);
      formData.append("engineSize", engineSize);
      formData.append("bodyStyle", bodyStyle);
      formData.append("trimLevel", trimLevel);
      formData.append("transmission", transmission);
      formData.append("drivetrain", drivetrain);
      formData.append("fuelType", fuelType);
      formData.append("mileage", mileage);
      formData.append("mileageUnit", mileageUnit);
      formData.append("condition", condition);
      formData.append("interiorFeatures", interiorFeatures);
      // Real Estate Fields
      formData.append("propertyType", propertyType);
      formData.append("listingType", listingType);
      formData.append("availableFrom", availableFrom);
      formData.append("bedrooms", bedrooms);
      formData.append("bathrooms", bathrooms);
      formData.append("squareFootage", squareFootage);
      formData.append("lotSize", lotSize);
      formData.append("yearBuilt", yearBuilt);
      formData.append("furnishingStatus", furnishingStatus);
      formData.append("parking", parking);
      formData.append("floorLevel", floorLevel);
      formData.append("totalFloors", totalFloors);

      formData.append("receiveOffers", receiveOffers);
      if (receiveOffers) {
        formData.append("minOfferAmount", minOfferAmount);
      }
      images.forEach((image) => {
        formData.append("images", image.file);
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/new-ad`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || "Failed to post the ad. Please try again."
        );
      }

      setStatusModal({
        open: true,
        type: "success",
        message: "Your ad has been posted successfully!",
        adID: result.uniqueId,
      });
      setSuccess("Ad posted successfully!");
    } catch (err) {
      setError(err.message);
      setStatusModal({
        open: true,
        type: "error",
        message:
          err.message ||
          "Something went wrong. Please refresh the page and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    if (e.target.value !== "Vehicles") {
      setModel("");
      setYear("");
      setEngineSize("");
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Helmet>
        <title>Post New Ad - Listed.ky</title>
        <meta name="description" content="Post New Ad - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          width: "100%",
          minWidth: { sm: "100%", md: "100%" },
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #ddd", // Light border by default
          // boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: "white",
          textAlign: "center",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            border: "1px solid var(--primary-color)", // Color on hover
          },
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            ...headingFonts,
            color: "var(--secondary-color)",
          }}
        >
          Post a New Ad
        </Typography>
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <TextField
            label="Title"
            variant="outlined"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            margin="normal"
            sx={{ ...textFieldStyles }}
          />
          <FormControl fullWidth required margin="normal">
            <InputLabel sx={{ ...fonts }}>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={handleCategoryChange}
              sx={{ ...selectStyles }}
            >
              <MenuItem value="" sx={{ ...fonts }}>
                <em>Select a category</em>
              </MenuItem>
              {categories.map((categoryItem) => (
                <MenuItem
                  key={categoryItem._id}
                  value={categoryItem.label}
                  sx={{ ...fonts }}
                >
                  {categoryItem.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {category === "Real Estate" && (
            <>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Property Type */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Property Type</InputLabel>
                  <Select
                    label="Property Type"
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="House" sx={{ ...fonts }}>
                      House
                    </MenuItem>
                    <MenuItem value="Apartment" sx={{ ...fonts }}>
                      Apartment
                    </MenuItem>
                    <MenuItem value="Condo" sx={{ ...fonts }}>
                      Condo
                    </MenuItem>
                    <MenuItem value="Commercial" sx={{ ...fonts }}>
                      Commercial
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Listing Type */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Listing Type</InputLabel>
                  <Select
                    label="Listing Type"
                    value={listingType}
                    onChange={(e) => setListingType(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="For Sale" sx={{ ...fonts }}>
                      For Sale
                    </MenuItem>
                    <MenuItem value="For Rent" sx={{ ...fonts }}>
                      For Rent
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* Available From */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Available From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={availableFrom}
                  onChange={(e) => setAvailableFrom(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Furnishing Status */}
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Furnishing Status</InputLabel>
                  <Select
                    label="Furnishing Status"
                    value={furnishingStatus}
                    onChange={(e) => setFurnishingStatus(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Furnished" sx={{ ...fonts }}>
                      Furnished
                    </MenuItem>
                    <MenuItem value="Semi-Furnished" sx={{ ...fonts }}>
                      Semi-Furnished
                    </MenuItem>
                    <MenuItem value="Unfurnished" sx={{ ...fonts }}>
                      Unfurnished
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* Number of Bedrooms */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Number of Bedrooms"
                  type="number"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />

                {/* Number of Bathrooms */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Number of Bathrooms"
                  type="number"
                  value={bathrooms}
                  onChange={(e) => setBathrooms(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Year Built */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Year Built"
                  type="number"
                  value={yearBuilt}
                  onChange={(e) => setYearBuilt(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
                {/* Square Footage */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Square Footage (sq. ft.)"
                  type="number"
                  value={squareFootage}
                  onChange={(e) => setSquareFootage(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
                {/* Lot Size */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Lot Size (if applicable)"
                  type="number"
                  value={lotSize}
                  onChange={(e) => setLotSize(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
              </Box>

              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Floor Level */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Floor Level (if applicable)"
                  type="number"
                  value={floorLevel}
                  onChange={(e) => setFloorLevel(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />

                {/* Total Floors in Building */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Total Floors in Building"
                  type="number"
                  value={totalFloors}
                  onChange={(e) => setTotalFloors(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
                {/* Parking */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parking (e.g., Garage, Street Parking, Number of Spaces)"
                  value={parking}
                  onChange={(e) => setParking(e.target.value)}
                  sx={{ ...textFieldStyles }}
                />
              </Box>
            </>
          )}

          {category === "Vehicles" && (
            <>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <TextField
                  label="Make"
                  variant="outlined"
                  fullWidth
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  label="Model"
                  variant="outlined"
                  fullWidth
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  label="Year"
                  type="number"
                  variant="outlined"
                  fullWidth
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  label="Trim Level"
                  variant="outlined"
                  fullWidth
                  value={trimLevel}
                  onChange={(e) => setTrimLevel(e.target.value)}
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Fuel Type</InputLabel>
                  <Select
                    label="Fuel Type"
                    value={fuelType}
                    onChange={(e) => setFuelType(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Gasoline" sx={{ ...fonts }}>
                      Gasoline
                    </MenuItem>
                    <MenuItem value="Diesel" sx={{ ...fonts }}>
                      Diesel
                    </MenuItem>
                    <MenuItem value="Electric" sx={{ ...fonts }}>
                      Electric
                    </MenuItem>
                    <MenuItem value="Hybrid" sx={{ ...fonts }}>
                      Hybrid
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Body Style</InputLabel>
                  <Select
                    label="Body Style"
                    value={bodyStyle}
                    onChange={(e) => setBodyStyle(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Sedan" sx={{ ...fonts }}>
                      Sedan
                    </MenuItem>
                    <MenuItem value="SUV" sx={{ ...fonts }}>
                      SUV
                    </MenuItem>
                    <MenuItem value="Truck" sx={{ ...fonts }}>
                      Truck
                    </MenuItem>
                    {/* Add more body styles as needed */}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Transmission</InputLabel>
                  <Select
                    label="Transmission"
                    value={transmission}
                    onChange={(e) => setTransmission(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Automatic" sx={{ ...fonts }}>
                      Automatic
                    </MenuItem>
                    <MenuItem value="Manual" sx={{ ...fonts }}>
                      Manual
                    </MenuItem>
                    <MenuItem value="CVT" sx={{ ...fonts }}>
                      CVT
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Drivetrain</InputLabel>
                  <Select
                    label="Drivetrain"
                    value={drivetrain}
                    onChange={(e) => setDrivetrain(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="FWD" sx={{ ...fonts }}>
                      FWD
                    </MenuItem>
                    <MenuItem value="AWD" sx={{ ...fonts }}>
                      AWD
                    </MenuItem>
                    <MenuItem value="RWD" sx={{ ...fonts }}>
                      RWD
                    </MenuItem>
                    <MenuItem value="4WD" sx={{ ...fonts }}>
                      4WD
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <TextField
                  label="Engine Size"
                  variant="outlined"
                  fullWidth
                  required
                  value={engineSize}
                  onChange={(e) => setEngineSize(e.target.value)}
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  label="Mileage"
                  type="number"
                  variant="outlined"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                  required
                  fullWidth
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Mileage in</InputLabel>
                  <Select
                    label="Mileage Unit"
                    value={mileageUnit}
                    onChange={(e) => setMileageUnit(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Miles" sx={{ ...fonts }}>
                      Miles
                    </MenuItem>
                    <MenuItem value="Kilometers" sx={{ ...fonts }}>
                      Kilometers
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Condition</InputLabel>
                  <Select
                    label="Condition"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Excellent" sx={{ ...fonts }}>
                      Excellent
                    </MenuItem>
                    <MenuItem value="Good" sx={{ ...fonts }}>
                      Good
                    </MenuItem>
                    <MenuItem value="Fair" sx={{ ...fonts }}>
                      Fair
                    </MenuItem>
                    <MenuItem value="Needs Work" sx={{ ...fonts }}>
                      Needs Work
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <ReactQuill
                style={{
                  marginTop: "15px",
                  marginBottom: "15px",
                  ...fonts,
                }}
                className="quill-editor"
                value={interiorFeatures}
                onChange={setInteriorFeatures}
                theme="snow"
                placeholder="Enter interior features (e.g., Leather Seats, Heated Seats)"
                required
              />
            </>
          )}
          {category !== "Vehicles" && (
            <ReactQuill
              style={{ marginTop: "15px", marginBottom: "15px" }}
              className="quill-editor"
              value={description}
              onChange={setDescription} // Update state on change
              theme="snow"
              placeholder="Enter a detailed description of your ad"
              required
            />
          )}
          {/* Location dropdown */}
          <FormControl fullWidth required margin="normal">
            <InputLabel sx={{ ...fonts }}>Location</InputLabel>
            <Select
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              sx={{ ...selectStyles }}
            >
              <MenuItem value="" sx={{ ...fonts }}>
                <em>Select a location</em>
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc.name} sx={{ ...fonts }}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Price"
            type="number"
            variant="outlined"
            fullWidth
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            margin="normal"
            sx={{ ...textFieldStyles }}
          />

          <ImageUpload images={images} setImages={setImages} />
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            value={email}
            readOnly
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ ...textFieldStyles }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={receiveOffers}
                onChange={(e) => setReceiveOffers(e.target.checked)}
                sx={{
                  "&.Mui-checked": {
                    color: "var(--primary-color)", // Filled color when checked
                  },
                }}
              />
            }
            label={<Typography sx={{ ...fonts }}>Receive offers</Typography>}
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              marginLeft: 0,
            }}
          />
          {receiveOffers && (
            <TextField
              label="Minimum Offer Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={minOfferAmount}
              onChange={(e) => setMinOfferAmount(e.target.value)}
              margin="normal"
              sx={{ ...textFieldStyles }}
            />
          )}
          <Button
            type="submit"
            className="main-button-style"
            sx={{ mt: 2, ...mainButtonStyles }}
            disabled={
              loading ||
              (userAdCount !== null &&
                userAdLimit !== null &&
                userAdCount >= userAdLimit)
            }
          >
            {loading ? (
              <>
                <span>Submitting...</span>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </>
            ) : (
              <>
                <span>Submit Ad</span>
                <ArrowForwardOutlinedIcon />
              </>
            )}
          </Button>
        </form>
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {success}
          </Alert>
        )}
        <AdLimitReachedModal
          open={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          adLimit={userAdLimit}
        />
        <AdStatusModal
          open={statusModal.open}
          type={statusModal.type}
          message={statusModal.message}
          adID={statusModal.adID}
          setStatusModal={setStatusModal}
        />
      </Box>
    </Container>
  );
};

export default PostAd;
